import type {
  TimelineData,
  TimelineItem,
  TimelineSection,
  TimelineCommandDto,
} from '@timeline/core';
import {
  normalizeInsertIndex,
  normalizeItemEndDate,
  cloneSection,
  getActiveSections,
  withUpdatedSections,
  findSectionIndex,
  findItemLocation,
} from '@timeline/core';

const assertNever = (value: never): never => {
  throw new Error(`Unsupported timeline command input: ${JSON.stringify(value)}`);
};

export interface TimelineCommand {
  execute(current: TimelineData): TimelineCommandExecutionResult | null;
}

export interface TimelineCommandExecutionResult {
  next: TimelineData;
  inverse: TimelineCommandDto;
}

class InsertSectionCommand implements TimelineCommand {
  constructor(
    private readonly section: TimelineSection,
    private readonly index?: number,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    if (findSectionIndex(current, this.section.id) !== -1) {
      return null;
    }

    const targetIndex = normalizeInsertIndex(sections.length, this.index);
    const nextSections = [...sections];
    nextSections.splice(targetIndex, 0, cloneSection(this.section));

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'remove-section',
        sectionId: this.section.id,
      },
    };
  }
}

class RemoveSectionCommand implements TimelineCommand {
  constructor(private readonly sectionId: string) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    const removedSection = sections[sectionIndex];
    const nextSections = sections.filter((section) => section.id !== this.sectionId);
    if (nextSections.length === 0) {
      return null;
    }

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'add-section',
        sectionId: removedSection.id,
        label: removedSection.label,
        index: sectionIndex,
        ...(removedSection.items.length > 0 ? { items: removedSection.items } : {}),
        ...(removedSection.hidden ? { hidden: removedSection.hidden } : {}),
      },
    };
  }
}

class UpdateSectionLabelCommand implements TimelineCommand {
  constructor(
    private readonly sectionId: string,
    private readonly nextLabel: string,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    const section = sections[sectionIndex];
    if (section.label === this.nextLabel) {
      return null;
    }

    const nextSections = sections.map((entry, index) => (
      index === sectionIndex ? { ...entry, label: this.nextLabel } : entry
    ));

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'update-section-label',
        sectionId: this.sectionId,
        nextLabel: section.label,
      },
    };
  }
}

class UpdateItemTitleCommand implements TimelineCommand {
  constructor(
    private readonly sectionId: string,
    private readonly itemId: string,
    private readonly nextTitle: string,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    const section = sections[sectionIndex];
    const itemIndex = section.items.findIndex((item) => item.id === this.itemId);
    if (itemIndex === -1) {
      return null;
    }

    const currentItem = section.items[itemIndex];
    if (currentItem.title === this.nextTitle) {
      return null;
    }

    const nextSections = sections.map((entry, index) => {
      if (index !== sectionIndex) {
        return entry;
      }
      return {
        ...entry,
        items: entry.items.map((item) => (
          item.id === this.itemId ? { ...item, title: this.nextTitle } : item
        )),
      };
    });

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'update-item-title',
        sectionId: this.sectionId,
        itemId: this.itemId,
        nextTitle: currentItem.title,
      },
    };
  }
}

class InsertItemCommand implements TimelineCommand {
  constructor(
    private readonly sectionId: string,
    private readonly item: TimelineItem,
    private readonly index?: number,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    const section = sections[sectionIndex];
    if (section.items.some((item) => item.id === this.item.id)) {
      return null;
    }

    const targetIndex = normalizeInsertIndex(section.items.length, this.index);
    const nextItems = [...section.items];
    nextItems.splice(targetIndex, 0, normalizeItemEndDate(this.item));

    const nextSections = sections.map((entry, index) => (
      index === sectionIndex ? { ...entry, items: nextItems } : entry
    ));

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'delete-item',
        sectionId: this.sectionId,
        itemId: this.item.id,
      },
    };
  }
}

class RemoveItemCommand implements TimelineCommand {
  constructor(
    private readonly itemId: string,
    private readonly expectedSectionId?: string,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const location = findItemLocation(current, this.itemId, this.expectedSectionId);
    if (!location) {
      return null;
    }

    const nextSections = getActiveSections(current).map((section, sectionIndex) => {
      if (sectionIndex !== location.sectionIndex) {
        return section;
      }
      return {
        ...section,
        items: section.items.filter((item) => item.id !== this.itemId),
      };
    });

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'create-item',
        sectionId: location.section.id,
        item: location.item,
        index: location.itemIndex,
      },
    };
  }
}

class MoveItemCommand implements TimelineCommand {
  constructor(
    private readonly itemId: string,
    private readonly nextRange: {
      sectionId: string;
      startDate: string;
      endDate?: string;
    },
    private readonly targetIndex?: number,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const source = findItemLocation(current, this.itemId);
    if (!source) {
      return null;
    }

    const targetSectionIndex = findSectionIndex(current, this.nextRange.sectionId);
    if (targetSectionIndex === -1) {
      return null;
    }

    const hasSameRange = (
      source.section.id === this.nextRange.sectionId
      && source.item.startDate === this.nextRange.startDate
      && source.item.endDate === this.nextRange.endDate
      && this.targetIndex === undefined
    );
    if (hasSameRange) {
      return null;
    }

    const nextSections = getActiveSections(current).map((section) => ({
      ...section,
      items: [...section.items],
    }));

    const [removedItem] = nextSections[source.sectionIndex].items.splice(source.itemIndex, 1);
    if (!removedItem) {
      return null;
    }

    const movedItem = normalizeItemEndDate({
      ...removedItem,
      startDate: this.nextRange.startDate,
      endDate: this.nextRange.endDate,
    });

    let insertionBaseLength = nextSections[targetSectionIndex].items.length;
    if (source.sectionIndex === targetSectionIndex && source.itemIndex < insertionBaseLength) {
      insertionBaseLength -= 1;
    }
    const insertionIndex = normalizeInsertIndex(insertionBaseLength, this.targetIndex);
    nextSections[targetSectionIndex].items.splice(insertionIndex, 0, movedItem);

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'change-item-date-range',
        itemId: this.itemId,
        nextRange: {
          sectionId: source.section.id,
          startDate: source.item.startDate,
          endDate: source.item.endDate,
        },
        targetIndex: source.itemIndex,
      },
    };
  }
}

class MoveSectionCommand implements TimelineCommand {
  constructor(
    private readonly sectionId: string,
    private readonly toIndex: number,
  ) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const fromIndex = findSectionIndex(current, this.sectionId);
    if (fromIndex === -1) {
      return null;
    }

    const targetIndex = normalizeInsertIndex(sections.length - 1, this.toIndex);
    if (fromIndex === targetIndex) {
      return null;
    }

    const nextSections = [...sections];
    const [moved] = nextSections.splice(fromIndex, 1);
    nextSections.splice(targetIndex, 0, moved);

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: {
        type: 'move-section',
        sectionId: this.sectionId,
        toIndex: fromIndex,
      },
    };
  }
}

class HideSectionCommand implements TimelineCommand {
  constructor(private readonly sectionId: string) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    if (sections[sectionIndex].hidden) {
      return null;
    }

    const nextSections = sections.map((entry, index) => (
      index === sectionIndex ? { ...entry, hidden: true } : entry
    ));

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: { type: 'show-section', sectionId: this.sectionId },
    };
  }
}

class ShowSectionCommand implements TimelineCommand {
  constructor(private readonly sectionId: string) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    const sections = getActiveSections(current);
    const sectionIndex = findSectionIndex(current, this.sectionId);
    if (sectionIndex === -1) {
      return null;
    }

    if (!sections[sectionIndex].hidden) {
      return null;
    }

    const nextSections = sections.map((entry, index) => (
      index === sectionIndex ? { ...entry, hidden: undefined } : entry
    ));

    return {
      next: withUpdatedSections(current, nextSections),
      inverse: { type: 'hide-section', sectionId: this.sectionId },
    };
  }
}

class ReplaceSnapshotCommand implements TimelineCommand {
  constructor(private readonly nextSnapshot: TimelineData) {}

  execute(current: TimelineData): TimelineCommandExecutionResult | null {
    if (JSON.stringify(current) === JSON.stringify(this.nextSnapshot)) {
      return null;
    }

    return {
      next: this.nextSnapshot,
      inverse: {
        type: 'replace-snapshot',
        snapshot: current,
      },
    };
  }
}

export const createTimelineCommand = (input: TimelineCommandDto): TimelineCommand => {
  switch (input.type) {
    case 'add-section':
      return new InsertSectionCommand({
        id: input.sectionId,
        label: input.label,
        items: input.items ?? [],
        ...(input.hidden ? { hidden: input.hidden } : {}),
      }, input.index);
    case 'remove-section':
      return new RemoveSectionCommand(input.sectionId);
    case 'update-section-label':
      return new UpdateSectionLabelCommand(input.sectionId, input.nextLabel);
    case 'update-item-title':
      return new UpdateItemTitleCommand(input.sectionId, input.itemId, input.nextTitle);
    case 'create-item':
      return new InsertItemCommand(input.sectionId, input.item, input.index);
    case 'change-item-date-range':
      return new MoveItemCommand(input.itemId, input.nextRange, input.targetIndex);
    case 'delete-item':
      return new RemoveItemCommand(input.itemId, input.sectionId);
    case 'move-section':
      return new MoveSectionCommand(input.sectionId, input.toIndex);
    case 'hide-section':
      return new HideSectionCommand(input.sectionId);
    case 'show-section':
      return new ShowSectionCommand(input.sectionId);
    case 'replace-snapshot':
      return new ReplaceSnapshotCommand(input.snapshot);
    default:
      return assertNever(input);
  }
};
